import { useEffect, useState } from "react";
import { Line } from "@nivo/line";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListSubheader from "@mui/material/ListSubheader";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import Avatar from "@mui/material/Avatar";
import {
	useQueryTrend,
	roundTimeRange as top12RoundTimeRange,
} from "../../hooks/top12ApiHooks";
import { useWindowSize } from "../../hooks/windowHooks";
import { useAppStore } from "../../store/appStore";
import { sidebarWidth } from "../Menu";
import { Loading } from "../Loading";
import { pad } from "../../fn/pad";

/*
const markers = ({ darkModeEnabled }) => ({
	f: [
		{
			axis: "x",
			value: new Date("2022-08-11T20:00:00"),
			lineStyle: { stroke: "#cc3366", strokeWidth: 4 },
		},
		{
			axis: "x",
			value: new Date("2022-08-11T23:00:00"),
			lineStyle: { stroke: "#cc3366", strokeWidth: 4 },
			legend: "🔴 校花 Live",
			textStyle: {
				fill: darkModeEnabled ? "#FFF" : "#333",
				strokeWidth: 0,
				fontSize: 12,
			},
		},
		{
			axis: "x",
			value: new Date("2022-08-13T22:00:00"),
			lineStyle: { stroke: "#009100", strokeWidth: 4 },
		},
	],
	m: [
		{
			axis: "x",
			value: new Date("2022-08-09T20:00:00"),
			lineStyle: { stroke: "#cc3366", strokeWidth: 4 },
		},
		{
			axis: "x",
			value: new Date("2022-08-09T23:00:00"),
			lineStyle: { stroke: "#cc3366", strokeWidth: 4 },
			legend: "🔴 校草 Live",
			textStyle: {
				fill: darkModeEnabled ? "#FFF" : "#333",
				strokeWidth: 0,
				fontSize: 12,
			},
		},
		{
			axis: "x",
			value: new Date("2022-08-13T22:00:00"),
			lineStyle: { stroke: "#009100", strokeWidth: 4 },
		},
	],
});
*/

const sortId = (left, right) => {
	return left.id > right.id ? -1 : 1;
};

const sortSlicesByVotes = (left, right) => {
	if (left.data.y > right.data.y) {
		return -1;
	} else if (left.data.y < right.data.y) {
		return 1;
	} else {
		return left.serieId > right.serieId ? -1 : 1;
	}
};

const TrendPage = () => {
	const { width, height } = useWindowSize();
	const darkModeEnabled = useAppStore((state) => state.darkModeEnabled);
	const listOption = useAppStore((state) => state.listOption);
	const top12VoteTrendsMeshOrSlices = useAppStore(
		(state) => state.top12VoteTrendsMeshOrSlices
	);

	const { isLoading, isError, error, data } = useQueryTrend({
		listOption,
		timeRangeMin: top12RoundTimeRange.trendsStart,
		timeRangeMax: "-",
		timeInterval: "01-days",
		timeFilter: "custom",
	});

	const [localData, setLocalData] = useState([]);

	useEffect(() => {
		if (data) {
			const chartData = data?.records.map((choice) => {
				return {
					id: `#${pad(choice.id, 2)}`,
					data: [...choice.voteTrends],
				};
			});

			setLocalData([...chartData].sort(sortId));
		}
	}, [data]);

	if (isLoading || !width || !height) return <Loading />;

	if (isError) return <div>Error found, {error.message}</div>;

	/*
	const chartMarkers =
		listOption === "f"
			? markers({ darkModeEnabled }).f
			: markers({ darkModeEnabled }).m;
	*/

	const ChartTooltip = ({ point }) => {
		return (
			<Paper>
				<List dense disablePadding>
					<ListSubheader>
						<Stack direction="row" alignItems="center" spacing={2}>
							<ListItemIcon>
								<Avatar
									alt={point.serieId}
									src={`${process.env.PUBLIC_URL}/images/${
										listOption === "f" ? "f" : "m"
									}/top21/${parseInt(point.serieId.substring(1))}.jpg`}
									sx={{ width: 64, height: 64 }}
								/>
							</ListItemIcon>
							<Typography>{point.serieId}</Typography>
						</Stack>

						<ListItemSecondaryAction>
							<Typography>{listOption === "f" ? "🌸" : "🍀"}</Typography>
						</ListItemSecondaryAction>
					</ListSubheader>
					<ListItem
						secondaryAction={
							<Typography>{`${point.data.y.toFixed(2)}%`}</Typography>
						}
					>
						<ListItemText
							primary={point.data.xFormatted}
							sx={{ minWidth: 150 }}
						/>
					</ListItem>
				</List>
			</Paper>
		);
	};

	const ChartSliceTooltip = ({ slice }) => {
		const top12VoteTrendsSlicesSortEnabled = useAppStore(
			(state) => state.top12VoteTrendsSlicesSortEnabled
		);

		let x = "";
		if (slice.points.length > 0) {
			const firstPoint = slice.points[0];
			if (firstPoint.data) {
				x = firstPoint.data.xFormatted;
			}
		}
		return (
			<Paper sx={{ minWidth: 200 }}>
				<List dense disablePadding>
					<ListSubheader>
						{listOption === "f" ? "🌸" : "🍀"} {x}
					</ListSubheader>
					{(top12VoteTrendsSlicesSortEnabled
						? [...slice.points].sort(sortSlicesByVotes)
						: [...slice.points]
					).map((point) => (
						<ListItem
							key={point.serieId}
							secondaryAction={
								<Typography>{`${point.data.y.toFixed(2)}%`}</Typography>
							}
						>
							<ListItemIcon>
								<Avatar
									alt={point.serieId}
									src={`${process.env.PUBLIC_URL}/images/${
										listOption === "f" ? "f" : "m"
									}/top21/${parseInt(point.serieId.substring(1))}.jpg`}
									sx={{ width: 48, height: 48 }}
								/>
							</ListItemIcon>
							<ListItemText
								primary={point.serieId}
								sx={{ color: point.serieColor }}
							/>
						</ListItem>
					))}
				</List>
			</Paper>
		);
	};

	const timeRowsCount =
		localData && localData.length > 0 ? localData[0].data.length : 1;

	const chartProps = {
		width:
			timeRowsCount * 0.5 > (width >= 1080 ? width - sidebarWidth : width)
				? timeRowsCount * 0.5
				: width >= 1080
				? width - sidebarWidth
				: width,
		height: height - 50,
		data: localData,
		// markers: chartMarkers,
		theme: {
			textColor: darkModeEnabled ? "#FFF" : "#000",
			legends: { title: { text: darkModeEnabled ? "#FFF" : "#000" } },
		},
		colors: { scheme: darkModeEnabled ? "paired" : "dark2" },
		lineWidth: 5,
		xScale: {
			type: "time",
			format: "%Y-%m-%d %H:%M:%S",
			useUTC: false,
		},
		xFormat: "time:%Y-%m-%d %H:%M",
		yScale: {
			type: "linear",
			min: "auto",
			max: "auto",
		},
		axisLeft: {
			legend: "票數",
			legendOffset: -60,
			legendPosition: "middle",
		},
		axisTop: {
			format: "%d %b %H:%M",
			tickValues: "every 6 hours",
			legendOffset: 0,
			tickRotation: width > 1000 ? 0 : 64,
		},
		axisBottom: {
			format: "%d %b %H:%M",
			tickValues: "every 6 hours",
		},
		enablePoints: false,
		animate: false,
		useMesh: top12VoteTrendsMeshOrSlices === "mesh",
		tooltip: ChartTooltip,
		enableSlices: top12VoteTrendsMeshOrSlices === "mesh" ? false : "x",
		sliceTooltip: ChartSliceTooltip,
		margin: { top: 40, right: 40, bottom: 30, left: 40 },
		legends: [
			{
				anchor: "right",
				direction: "column",
				justify: false,
				translateX: 60,
				translateY: 0,
				itemsSpacing: 2,
				itemDirection: "left-to-right",
				itemWidth: 80,
				itemHeight: 20,
				itemOpacity: 0.75,
				symbolSize: 12,
				symbolShape: "circle",
			},
		],
	};

	return (
		<Box
			width={width >= 1080 ? width - sidebarWidth : width}
			height={height}
			display="flex"
		>
			<Line
				{...chartProps}
				layers={["axes"]}
				axisTop={null}
				axisBottom={null}
				width={50}
				margin={{ ...chartProps.margin, left: 50 }}
			/>
			<Box
				sx={{ overflowX: "scroll", overflowY: "hidden" }}
				width={width >= 1080 ? width - sidebarWidth - 80 : width - 80}
				height={height - 10}
			>
				<Line
					{...chartProps}
					axisLeft={null}
					margin={{ ...chartProps.margin, left: 0 }}
					legends={[]}
				/>
			</Box>
			<Box width={80} height={height - 10}>
				<Line
					{...chartProps}
					layers={["legends"]}
					axisBottom={null}
					axisTop={null}
					axisLeft={null}
					axisRight={null}
					width={80}
					margin={{ ...chartProps.margin }}
				/>
			</Box>
		</Box>
	);
};

export default TrendPage;
